import React, { useState, useEffect, useRef } from 'react';
import ModelPinSelect from './ModelPinSelect';
import { useLocation } from 'react-router-dom';
import { aiService } from '../../services/aiService';
import { OrderService } from '../../services/orderService';
import { UploadIcon, TrashIcon, CloseIcon, SparklesIcon, CheckIcon, XIcon, DownloadIcon, RefreshIcon } from '../icons/Icons';
import AspectRatioSelector from './AspectRatioSelector';
import { useAuthStore } from '../../stores/useAuthStore';
import { GalleryItem } from '../../types/gallery';
import type { ImageAnnotation } from '../../types/order';
import { dataUrlToFile } from '../../utils/fileUtils';
import { useModalClose } from '../../hooks/useModalClose';
import UploadChoiceModal from './UploadChoiceModal';
import GallerySelectorModal from './GallerySelectorModal';
import type { GallerySelectedImage } from './GallerySelectorModal';
import { galleryImageToFile } from '../../utils/galleryToFile';
import { getImageExtFromUrl } from '../../utils/imageFormat';

/**
 * Contexto opcional: quando presente, o editor está sendo aberto a partir de
 * uma imagem de OUTPUT de um PEDIDO (não da galeria). Nesse modo, `handleGenerate`
 * chama o endpoint dedicado do pedido — assíncrono (202), sem imagem na hora —
 * em vez do fluxo de edição genérico da galeria.
 */
interface QuickEditModalOrderEdit {
    orderId: string;
    itemId: string;
    orderImageId: string;
    /** Pins do cliente na imagem (reprovação) — exibidos sobre a imagem e listados abaixo do prompt. */
    pins?: ImageAnnotation[];
    /** Chamado após o 202, pra o pedido recarregar e mostrar a nova versão. */
    onQueued?: () => void;
}

interface QuickEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    baseImage: GalleryItem | null;
    onImageEdited: (newImageUrl: string, newBackendId?: number) => void;
    /** Presente apenas quando o editor é aberto a partir do pedido (ver acima). */
    orderEdit?: QuickEditModalOrderEdit;
}

type ImageState = {
    file: File | string;
    url: string;
    backendId?: number;
    selected?: boolean;
};

const QuickEditModal: React.FC<QuickEditModalProps> = ({ isOpen, onClose, baseImage, onImageEdited, orderEdit }) => {
    const maxResolution = useAuthStore(state => state.user?.maxResolution);
    // Quando o modal é aberto a partir do /moodboard, marcamos a task com
    // source_app='moodboard' para que o backend resolva o ProviderChain do
    // contexto Moodboard (mesmo quando o moodboard ainda não foi salvo e
    // portanto não existe brandbook_id).
    const location = useLocation();
    const source = location.pathname.startsWith('/moodboard') || location.pathname.startsWith('/marketing/canvas') ? 'moodboard' : undefined;
    const [prompt, setPrompt] = useState('');
    const [referenceImages, setReferenceImages] = useState<ImageState[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editedImageUrl, setEditedImageUrl] = useState<string | null>(null);
    const [editedImageId, setEditedImageId] = useState<number | null>(null);
    // Aviso transitório exibido só no caminho orderEdit (endpoint assíncrono do
    // pedido não devolve a imagem na hora — não há o que comparar/Manter aqui).
    const [queuedNotice, setQueuedNotice] = useState<string | null>(null);
    const [currentImage, setCurrentImage] = useState<ImageState | null>(null);
    const [history, setHistory] = useState<ImageState[]>([]);
    const [isRefDragging, setIsRefDragging] = useState(false);
    const [aspectRatio, setAspectRatio] = useState('1:1');
    const [outputFormat, setOutputFormat] = useState<'png' | 'jpeg'>('png');
    const [resolution, setResolution] = useState<'1K' | '2K' | '4K'>('1K');
    // Modelo fixado para esta edição (só equipe interna; undefined = cadeia padrão).
    const [pinnedModel, setPinnedModel] = useState<string | undefined>(undefined);
    const [isDownloading, setIsDownloading] = useState(false);
    const refImageInputRef = useRef<HTMLInputElement>(null);
    const [isRefChoiceModalOpen, setIsRefChoiceModalOpen] = useState(false);
    const [isRefGalleryModalOpen, setIsRefGalleryModalOpen] = useState(false);

    useEffect(() => {
        if (baseImage) {
            setPrompt('');
            setReferenceImages([]);
            setEditedImageUrl(null);
            setEditedImageId(null);
            setError(null);
            setQueuedNotice(null);
            setHistory([]);

            // Initialize current image from baseImage
            setCurrentImage({
                file: baseImage.src,
                url: baseImage.src,
                backendId: typeof baseImage.backendId === 'string'
                    ? parseInt(baseImage.backendId)
                    : baseImage.backendId || (typeof baseImage.id === 'string' && baseImage.id.startsWith('img_')
                        ? parseInt(baseImage.id.replace('img_', ''))
                        : undefined)
            });
        }
    }, [baseImage]);

    // Use standard modal close behavior (ESC key)
    useModalClose(isOpen, onClose);

    const handleDownload = async (src: string, filename: string, backendId?: number) => {
        if (isDownloading) return;
        setIsDownloading(true);
        try {
            // Preferred path: use the backend proxy (handles Content-Disposition)
            // instead of fetching the S3 URL directly — direct fetch fails on
            // CORS and the fallback below opens the image in a new tab.
            const blob = backendId
                ? await aiService.downloadImageBlob(backendId)
                : await (await fetch(src)).blob();

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Download error:", err);
            window.open(src, '_blank');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleGenerate = async () => {
        if (!currentImage) {
            setError('Imagem base não encontrada ou inválida.');
            return;
        }
        if (!prompt.trim()) {
            setError('Por favor, descreva a edição desejada.');
            return;
        }
        if (prompt.trim().length < 10) {
            setError('A descrição deve ter pelo menos 10 caracteres para um bom resultado.');
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            if (orderEdit) {
                // Pedido: endpoint dedicado e assíncrono (202) — não usa o editor
                // genérico da galeria nem o fluxo de comparar/"Manter e Continuar"
                // (a imagem editada só aparece como nova versão no pedido depois).
                await OrderService.editOrderImage(
                    orderEdit.orderId,
                    orderEdit.itemId,
                    orderEdit.orderImageId,
                    prompt,
                    { imageSize: resolution, outputFormat },
                );
                orderEdit.onQueued?.();
                setQueuedNotice('Edição enviada — a nova versão aparece no pedido em instantes.');
                setTimeout(() => onClose(), 1500);
                return;
            }

            // Filter only SELECTED reference images
            const selectedRefs = referenceImages.filter(img => img.selected);

            // Use backendId if available, otherwise use File object
            const refFiles = selectedRefs.map(img =>
                img.backendId ? img.backendId : (img.file instanceof File ? img.file : undefined)
            ).filter((f): f is File | number => f !== undefined);

            // Use backendId for current image if available
            let imageToEdit: File | number = currentImage.backendId || (currentImage.file as any);

            if (!imageToEdit && currentImage.url) {
                // Final Fallback: Fetch from URL (might cause CORS)
                console.warn('QuickEditModal: No backendId found, falling back to fetch', currentImage);
                imageToEdit = await dataUrlToFile(currentImage.url, `edit_${Date.now()}.png`);
            }

            const result = await aiService.editImage(imageToEdit, prompt, undefined, refFiles, aspectRatio, resolution, source, outputFormat, pinnedModel);

            if (result) {
                setEditedImageUrl(result.url);
                setEditedImageId(result.id);
            } else {
                setError('A API não retornou uma imagem. Tente novamente.');
            }
        } catch (e: any) {
            setError(e.message || 'Erro ao editar imagem.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeep = () => {
        if (editedImageUrl && editedImageId && currentImage) {
            // Push previous image to history and update current
            setHistory(prev => [...prev, currentImage]);
            setCurrentImage({
                file: editedImageUrl,
                url: editedImageUrl,
                backendId: editedImageId
            });
            setEditedImageUrl(null);
            setEditedImageId(null);
            setPrompt('');

            // Unselect all reference images after successful edit
            setReferenceImages(prev => prev.map(img => ({ ...img, selected: false })));
        }
    };

    const handleDiscard = () => {
        setEditedImageUrl(null);
        setEditedImageId(null);
    };

    const handleConfirmAndClose = () => {
        if (currentImage) {
            onImageEdited(currentImage.url, currentImage.backendId);
            onClose();
        }
    };

    const handleUndo = () => {
        if (history.length > 0) {
            const previous = history[history.length - 1];
            setCurrentImage(previous);
            setHistory(prev => prev.slice(0, -1));
            setEditedImageUrl(null);
            setEditedImageId(null);
        }
    };

    const handleAddReferenceImage = (file: File) => {
        if (file && file.type.startsWith('image/') && referenceImages.length < 3) {
            setReferenceImages(prev => [...prev, {
                file,
                url: URL.createObjectURL(file),
                selected: true // Auto-select new reference images
            }]);
        }
    };

    const handleReferenceImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleAddReferenceImage(file);
        }
    };

    const handleRefDragEnter = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsRefDragging(true);
    };
    const handleRefDragLeave = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsRefDragging(false);
    };
    const handleRefDragOver = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };
    const handleRefDrop = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsRefDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleAddReferenceImage(file);
        }
    };

    const handleRefGallerySelect = async (url: string, image?: GallerySelectedImage) => {
        try {
            const file = await galleryImageToFile(image, url);
            handleAddReferenceImage(file);
        } catch (err) {
            console.error('Failed to load gallery image:', err);
        }
        setIsRefGalleryModalOpen(false);
    };

    if (!isOpen || !baseImage || baseImage.type !== 'image' || !currentImage) return null;

    return (
        <div
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={(e) => {
                // Only close if clicking the backdrop, not the modal content
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
            role="dialog"
            aria-modal="true"
        >
            <div
                className="bg-snapfy-surface rounded-xl w-full max-w-6xl max-h-[90vh] flex flex-col gap-6 p-6 overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-bold text-snapfy-text-primary mb-1">Edição Rápida</h2>
                        <p className="text-snapfy-text-secondary text-sm">Edite iterativamente, adicione referências e refine sua imagem.</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-snapfy-text-secondary hover:text-snapfy-text-primary transition-colors"
                        aria-label="Fechar"
                    >
                        <CloseIcon />
                    </button>
                </div>

                <div className="flex-1 flex flex-col lg:flex-row gap-6 min-h-0">
                    {/* Image Viewer Area */}
                    <div className="flex-1 lg:w-2/3 flex flex-col gap-4 bg-snapfy-inner/50 rounded-xl p-4 min-h-0 relative">
                        <div className="flex-1 w-full min-h-0 flex gap-4">
                            {/* If editedImageUrl exists, show Comparison View */}
                            {editedImageUrl ? (
                                <>
                                    {/* Original (Left) */}
                                    <div className="flex-1 flex flex-col gap-2 min-w-0">
                                        <span className="text-sm font-medium text-snapfy-text-secondary text-center uppercase tracking-wider">Original</span>
                                        <div className="flex-1 relative rounded-xl overflow-hidden border border-snapfy-border bg-black/20">
                                            <img src={currentImage.url} alt="Original" className="absolute inset-0 w-full h-full object-contain" />
                                        </div>
                                    </div>

                                    {/* Result (Right) */}
                                    <div className="flex-1 flex flex-col gap-2 min-w-0">
                                        <span className="text-sm font-medium text-snapfy-accent-soft text-center uppercase tracking-wider">Resultado</span>
                                        <div className="flex-1 relative rounded-xl overflow-hidden border border-[#7063f7]/30 bg-black/20 shadow-[0_0_15px_rgba(168,85,247,0.15)]">
                                            <img src={editedImageUrl} alt="Resultado" className="absolute inset-0 w-full h-full object-contain" />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                /* Single View (Current Image) */
                                <div className="flex-1 relative rounded-xl overflow-hidden border border-snapfy-border bg-black/20">
                                    <img src={currentImage.url} alt="Imagem Atual" className="absolute inset-0 w-full h-full object-contain" />

                                    {/* Pins do cliente (pedido) — mesmo visual do painel de itens do pedido. */}
                                    {orderEdit?.pins?.map((pin, pinIdx) => (
                                        <span
                                            key={pin.id}
                                            title={pin.text || undefined}
                                            className="absolute flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-pill bg-snapfy-error text-[11px] font-bold text-white shadow-lg"
                                            style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                                        >
                                            {pinIdx + 1}
                                        </span>
                                    ))}

                                    {/* Loading Overlay */}
                                    {isLoading && (
                                        <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center text-white rounded-xl">
                                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#9b8ff9]"></div>
                                            <p className="mt-4 text-lg font-semibold">Aguardando processamento da edição...</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Download Button (current view) */}
                        {!editedImageUrl && (
                            <div className="absolute top-4 right-4">
                                <button
                                    onClick={() => currentImage && handleDownload(currentImage.url, `snapfy-image-${Date.now()}.${getImageExtFromUrl(currentImage.url)}`, currentImage.backendId)}
                                    disabled={isDownloading}
                                    className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                    title={isDownloading ? 'Aguarde o download...' : 'Baixar Imagem Atual'}
                                >
                                    {isDownloading ? (
                                        <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <DownloadIcon className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                        )}

                        {/* Action Bar (Only visible when reviewing an edit) */}
                        {editedImageUrl && (
                            <div className="flex items-center justify-center gap-4 p-4 bg-snapfy-inner/80 backdrop-blur-sm rounded-xl border border-snapfy-border shadow-xl">
                                <button
                                    onClick={handleDiscard}
                                    className="flex items-center gap-2 px-6 py-2.5 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-xl transition-all font-medium"
                                >
                                    <XIcon className="w-5 h-5" />
                                    Descartar
                                </button>
                                <div className="w-px h-8 bg-snapfy-inner mx-2"></div>
                                <button
                                    onClick={handleKeep}
                                    className="flex items-center gap-2 px-6 py-2.5 bg-[#7063f7] hover:bg-[#7063f7] text-white rounded-xl shadow-lg hover:shadow-[#7063f7]/25 transition-all font-bold transform hover:scale-105"
                                >
                                    <CheckIcon className="w-5 h-5" />
                                    Manter e Continuar
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Controls Panel */}
                    <div className="flex-1 lg:w-1/3 flex flex-col gap-6 overflow-y-auto min-h-0 max-h-full">
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label htmlFor="edit-prompt" className="block text-sm font-medium text-snapfy-text-secondary">Prompt de Edição</label>
                                <button
                                    onClick={async () => {
                                        if (!prompt || !prompt.trim() || !currentImage) return;
                                        setIsLoading(true);
                                        try {
                                            const refFiles = referenceImages.filter(img => img.selected).map(img => img.file instanceof File ? img.file : undefined).filter((f): f is File => f !== undefined);
                                            const optimized = await aiService.optimizePrompt(prompt, currentImage.url, refFiles);
                                            setPrompt(optimized || prompt);
                                        } catch (e) {
                                            console.error("Failed to optimize", e);
                                        } finally {
                                            setIsLoading(false);
                                        }
                                    }}
                                    disabled={isLoading || !prompt || !prompt.trim() || !currentImage}
                                    className="text-xs flex items-center gap-1 text-snapfy-accent-soft hover:text-snapfy-accent-soft disabled:opacity-50 transition-colors"
                                    title="Usar IA para melhorar o prompt com base na imagem e referências"
                                >
                                    <SparklesIcon size={14} />
                                    Melhorar Prompt
                                </button>
                            </div>
                            <textarea
                                id="edit-prompt"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="Ex: Mude a cor da camisa para azul e adicione um chapéu..."
                                className="w-full min-h-[120px] bg-snapfy-inner border border-snapfy-border rounded-xl p-3 text-snapfy-text-secondary focus:ring-2 focus:ring-[#7063f7] focus:outline-none placeholder-snapfy-text-secondary resize-y"
                            />
                            {/* Marcações do cliente (pedido) — pra o operador ler enquanto digita o prompt. */}
                            {orderEdit?.pins && orderEdit.pins.length > 0 && (
                                <div className="mt-2 space-y-1.5 rounded-xl border border-snapfy-border bg-snapfy-inner p-2.5">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-snapfy-text-secondary">
                                        Marcações do cliente
                                    </p>
                                    {orderEdit.pins.map((pin, pinIdx) => (
                                        <p key={pin.id} className="text-xs text-snapfy-text-secondary flex items-start gap-1.5">
                                            <span className="mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-pill bg-snapfy-error text-[9px] font-bold text-white">
                                                {pinIdx + 1}
                                            </span>
                                            <span>{pin.text}</span>
                                        </p>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-snapfy-text-secondary mb-2">Imagens de Referência (Opcional)</label>
                            <div className="grid grid-cols-3 gap-2">
                                {referenceImages.map((img, index) => (
                                    <div
                                        key={index}
                                        className={`relative group aspect-square cursor-pointer border-2 rounded-xl transition-all ${img.selected
                                            ? 'border-[#7063f7] shadow-[0_0_10px_rgba(168,85,247,0.3)]'
                                            : 'border-transparent hover:border-snapfy-border'
                                            }`}
                                        onClick={() => {
                                            // Toggle selection
                                            setReferenceImages(prev => prev.map((item, i) =>
                                                i === index ? { ...item, selected: !item.selected } : item
                                            ));
                                        }}
                                    >
                                        <img src={img.url} alt={`Referência ${index + 1}`} className="w-full h-full object-cover rounded-md" />

                                        {/* Selection Indicator */}
                                        {img.selected && (
                                            <div className="absolute top-1 left-1 bg-[#7063f7] text-white rounded-full p-1 shadow-md z-10">
                                                <CheckIcon size={12} />
                                            </div>
                                        )}

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation(); // Prevent toggle when clicking delete
                                                setReferenceImages(prev => prev.filter((_, i) => i !== index));
                                            }}
                                            className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 z-10"
                                        >
                                            <TrashIcon className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                                {referenceImages.length < 3 && (
                                    <button
                                        onClick={() => setIsRefChoiceModalOpen(true)}
                                        onDrop={handleRefDrop}
                                        onDragOver={handleRefDragOver}
                                        onDragEnter={handleRefDragEnter}
                                        onDragLeave={handleRefDragLeave}
                                        className={`w-full aspect-square flex flex-col items-center justify-center text-snapfy-text-secondary border-2 border-dashed rounded-md cursor-pointer transition-colors ${isRefDragging ? 'bg-snapfy-inner border-[#7063f7]' : 'border-snapfy-border hover:bg-snapfy-inner hover:border-[#7063f7]'}`}
                                    >
                                        <UploadIcon />
                                        <span className="text-xs mt-1">{isRefDragging ? 'Soltar' : 'Adicionar'}</span>
                                    </button>
                                )}
                            </div>
                            <input ref={refImageInputRef} id="ref-image-upload" type="file" className="hidden" accept="image/*" onChange={handleReferenceImageUpload} />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-snapfy-text-secondary mb-2">Proporção de Saída</label>
                            <AspectRatioSelector
                                aspectRatio={aspectRatio}
                                onAspectRatioChange={setAspectRatio}
                                resolution={resolution}
                                onResolutionChange={setResolution}
                                maxResolution={maxResolution}
                                outputFormat={outputFormat}
                                onOutputFormatChange={setOutputFormat}
                            />
                        </div>

                        <ModelPinSelect value={pinnedModel} onChange={setPinnedModel} disabled={isLoading} />

                        <div className="mt-auto space-y-3">
                            {error && <div className="text-center text-sm text-red-400 bg-red-900/20 p-2 rounded-xl">{error}</div>}
                            {queuedNotice && <div className="text-center text-sm text-snapfy-success bg-green-900/20 p-2 rounded-xl">{queuedNotice}</div>}

                            {/* History / Undo Control */}
                            {history.length > 0 && !editedImageUrl && (
                                <button
                                    onClick={handleUndo}
                                    disabled={isLoading}
                                    className="w-full px-4 py-2 text-sm font-medium text-snapfy-text-secondary bg-snapfy-inner rounded-xl hover:bg-snapfy-inner transition-colors flex items-center justify-center gap-2"
                                >
                                    <RefreshIcon className="w-4 h-4" /> Desfazer Última Edição
                                </button>
                            )}

                            <button
                                onClick={handleGenerate}
                                disabled={isLoading || !prompt.trim() || !!editedImageUrl}
                                className="w-full px-8 py-3 font-semibold text-white snapfy-gradient rounded-full hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                            >
                                {isLoading ? (
                                    <div className="flex items-center justify-center">
                                        <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                        <span>Gerando...</span>
                                    </div>
                                ) : 'Gerar Edição (1 crédito)'}
                            </button>

                            <button
                                onClick={handleConfirmAndClose}
                                disabled={!!editedImageUrl}
                                className="w-full px-8 py-3 font-semibold text-white bg-green-600 rounded-full hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Aplicar e Fechar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <UploadChoiceModal
                isOpen={isRefChoiceModalOpen}
                onClose={() => setIsRefChoiceModalOpen(false)}
                onChooseFile={() => refImageInputRef.current?.click()}
                onChooseGallery={() => {
                    setIsRefChoiceModalOpen(false);
                    setIsRefGalleryModalOpen(true);
                }}
            />
            <GallerySelectorModal
                isOpen={isRefGalleryModalOpen}
                onClose={() => setIsRefGalleryModalOpen(false)}
                onSelectImage={handleRefGallerySelect}
            />
        </div>
    );
};

export default QuickEditModal;